import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import {
  CREATE_ENROLLMENT,
  ENROLL,
  EnrollmentStep,
  EnrollmentStepIds,
  SET_ACCOUNT_ADMIN_ONE,
  SET_ACCOUNT_ADMIN_TWO,
  SET_ENTITY_INFO,
  SET_IS_DFIN_ADMIN,
  UPLOAD_TEMPLATE_FILE,
  EdgarStatus,
  GET_EDGAR_STATUS,
  TEMPLATE_ERROR,
  GET_LATEST_ENROLLMENT,
  CONTINUE_ENROLLMENT,
  EnrollmentStepDTO,
  EnrollmentType,
} from './enrollmentModel';
import {
  createEnrollmentFail,
  createEnrollmentSuccess,
  setActiveStep,
  setEntityInfo,
  setEntityInfoFail,
  setEntityInfoSuccess,
  setAccountAdminOne,
  setAccountAdminOneFail,
  setAccountAdminOneSuccess,
  setAccountAdminTwo,
  setAccountAdminTwoFail,
  setAccountAdminTwoSuccess,
  setIsDFINAdmin,
  setIsDFINAdminFail,
  setIsDFINAdminSuccess,
  uploadTemplateFile,
  uploadTemplateFileSuccess,
  uploadTemplateFileFail,
  enrollSuccess,
  enrollFail,
  getEdgarStatusSuccess,
  getEdgarStatusFail,
  getLatestEnrollmentSuccess,
  getLatestEnrollmentFail,
  createEnrollment,
  continueEnrollment,
  continueEnrollmentSuccess,
  continueEnrollmentFail,
  setEnrollmentType,
  resetStepIdsAfterBulk,
} from './enrollmentActions';
import { getUserId } from '../app/appSelectors';
import enrollmentApi from './enrollmentApi';
import { setIsNotified } from '../app/appActions';
import { getEnrollmentId, getEnrollmentStepIds } from './enrollmentSelectors';
import { showModal } from '../modal/modalActions';
import { AxiosError } from 'axios';
import store from '../store';

export function* createEnrollmentSaga(
  action: ReturnType<typeof createEnrollment>
) {
  const { callback } = action.payload;
  const userId: string = yield select(getUserId);

  try {
    const { data: enrollment } = yield call(
      enrollmentApi.createEnrollment,
      userId
    );
    yield put(createEnrollmentSuccess(enrollment.enrollmentId));
    yield call(callback);
  } catch (error) {
    console.error(error);
    yield put(createEnrollmentFail());
  }
}

function* saveStep(
  stepName: EnrollmentStep,
  stepData: string,
  stepId?: string
) {
  const enrollmentId: string = yield select(getEnrollmentId);

  if (!stepId) {
    const { data: step } = yield call(
      enrollmentApi.saveStep,
      enrollmentId,
      stepName,
      stepData
    );
    return step.stepId;
  } else {
    const { data: step } = yield call(
      enrollmentApi.updateStep,
      enrollmentId,
      stepId,
      stepName,
      stepData
    );
    return step.stepId;
  }
}

export function* setIsDFINAdminSaga(action: ReturnType<typeof setIsDFINAdmin>) {
  const { isDFINAdmin } = action.payload;
  const stepData: string = JSON.stringify({ isDFINAdmin });
  const enrollmentStepIds: EnrollmentStepIds =
    yield select(getEnrollmentStepIds);
  const pathSelectionStepId: string = enrollmentStepIds.pathSelection;

  try {
    const stepId: string = yield call(
      saveStep,
      EnrollmentStep.PATH_SELECTION,
      stepData,
      pathSelectionStepId
    );
    yield put(setIsDFINAdminSuccess(isDFINAdmin, stepId));
    yield put(setActiveStep(EnrollmentStep.ENTITY_INFORMATION));
  } catch (error) {
    console.error(error);
    yield put(setIsDFINAdminFail());
  }
}

export function* setEntityInfoSaga(action: ReturnType<typeof setEntityInfo>) {
  const { entityInfo } = action.payload;
  const stepData: string = JSON.stringify({ entityInfo });
  const enrollmentStepIds: EnrollmentStepIds =
    yield select(getEnrollmentStepIds);
  const entityInfoStepId: string = enrollmentStepIds.entityInformation;

  try {
    const stepId: string = yield call(
      saveStep,
      EnrollmentStep.ENTITY_INFORMATION,
      stepData,
      entityInfoStepId
    );
    yield put(setEntityInfoSuccess(entityInfo, stepId));
    yield put(setActiveStep(EnrollmentStep.ACCOUNT_ADMINISTRATOR_1));
  } catch (error) {
    console.error(error);
    yield put(setEntityInfoFail());
  }
}

export function* uploadTemplateFileSaga(
  action: ReturnType<typeof uploadTemplateFile>
) {
  const { file } = action.payload;
  const enrollmentId: string = yield select(getEnrollmentId);

  try {
    const { data } = yield call(
      enrollmentApi.uploadTemplateFile,
      enrollmentId,
      file
    );
    yield put(resetStepIdsAfterBulk());
    yield put(uploadTemplateFileSuccess(data.entityNames));
  } catch (error: any) {
    console.error(error);
    yield put(resetStepIdsAfterBulk());
    if (error.status === 400) {
      if (error.response.data.errors.length > 0) {
        const errors = error.response.data.errors;
        const errorMessage = errors.join('|');
        yield put(uploadTemplateFileFail(errorMessage));
      } else {
        yield put(uploadTemplateFileFail(TEMPLATE_ERROR));
      }
    } else {
      yield put(uploadTemplateFileFail());
    }
  }
}

export function* setAccountAdminOneSaga(
  action: ReturnType<typeof setAccountAdminOne>
) {
  const { accountAdminOne } = action.payload;
  const stepData: string = JSON.stringify({ accountAdminOne });
  const enrollmentStepIds: EnrollmentStepIds =
    yield select(getEnrollmentStepIds);
  const accountAdminStepId: string = enrollmentStepIds.accountAdministrator1;

  try {
    const stepId: string = yield call(
      saveStep,
      EnrollmentStep.ACCOUNT_ADMINISTRATOR_1,
      stepData,
      accountAdminStepId
    );
    yield put(setAccountAdminOneSuccess(accountAdminOne, stepId));
    yield put(setActiveStep(EnrollmentStep.ACCOUNT_ADMINISTRATOR_2));
  } catch (error) {
    console.error(error);
    yield put(setAccountAdminOneFail());
  }
}

export function* setAccountAdminTwoSaga(
  action: ReturnType<typeof setAccountAdminTwo>
) {
  const { accountAdminTwo } = action.payload;
  const stepData: string = JSON.stringify({ accountAdminTwo });
  const enrollmentStepIds: EnrollmentStepIds =
    yield select(getEnrollmentStepIds);
  const accountAdminStepId: string = enrollmentStepIds.accountAdministrator2;

  try {
    const stepId: string = yield call(
      saveStep,
      EnrollmentStep.ACCOUNT_ADMINISTRATOR_2,
      stepData,
      accountAdminStepId
    );
    yield put(setAccountAdminTwoSuccess(accountAdminTwo, stepId));
    yield put(setActiveStep(EnrollmentStep.REVIEW));
  } catch (error) {
    console.error(error);
    yield put(setAccountAdminTwoFail());
  }
}

export function* enrollSaga() {
  const enrollmentId: string = yield select(getEnrollmentId);

  try {
    yield call(enrollmentApi.enroll, enrollmentId);
    yield put(enrollSuccess());
    yield put(setActiveStep(EnrollmentStep.COMPLETE));
  } catch (error) {
    yield put(enrollFail());

    if (error instanceof Error && 'isAxiosError' in error) {
      const axiosError = error as AxiosError<any>;

      let title = 'Error';
      let buttonText = 'Go Back';
      let errorMessage =
        axiosError.response?.data?.errorMessage || axiosError.message;
      let targetStep: EnrollmentStep;

      if (axiosError.response?.status === 400) {
        targetStep = EnrollmentStep.REVIEW;

        if (axiosError.response?.data?.title === 'Error enrolling account') {
          buttonText = 'Retry Enrollment';
        } else if (axiosError.response?.data?.title === 'Already Enrolled') {
          title = 'Invalid Data';
        } else if (axiosError.response?.data?.title === 'Edgar error occured') {
          title = 'Edgar Error Occured';
        } else {
          title = 'Invalid Data';
          targetStep = EnrollmentStep.ENTITY_INFORMATION;
        }
      }

      yield put(
        showModal({
          title: title,
          description: errorMessage,
          buttonText: buttonText,
          onConfirm: () => store.dispatch(setActiveStep(targetStep)),
        })
      );
    }
  }
}

export function* getEdgarStatusSaga() {
  try {
    const { data: status } = yield call(enrollmentApi.getEdgarStatus);
    yield put(getEdgarStatusSuccess(status === EdgarStatus.UP));
    yield put(setIsNotified(true));
  } catch (error) {
    console.error(error);
    yield put(getEdgarStatusFail());
  }
}

export function* getLatestEnrollmentSaga() {
  const userId: string = yield select(getUserId);
  try {
    const { data: response } = yield call(
      enrollmentApi.getLatestEnrollment,
      userId
    );
    yield put(getLatestEnrollmentSuccess(response.enrollmentId));
  } catch (error) {
    yield put(getLatestEnrollmentFail());
  }
}

function getLastStep(enrollmentStepIds: EnrollmentStepIds) {
  if (enrollmentStepIds.accountAdministrator2) {
    return EnrollmentStep.ACCOUNT_ADMINISTRATOR_2;
  }
  if (enrollmentStepIds.accountAdministrator1) {
    return EnrollmentStep.ACCOUNT_ADMINISTRATOR_1;
  }
  if (enrollmentStepIds.bulk) {
    return EnrollmentStep.REVIEW;
  }
  return EnrollmentStep.ENTITY_INFORMATION;
}

function mapEnrollmentStepIds(enrollmentSteps: any) {
  return enrollmentSteps.reduce(
    (acc: EnrollmentStepIds, stepData: any) => ({
      ...acc,
      [stepData.stepName]: stepData.stepId,
    }),
    {}
  );
}

function* loadPreviousStepData(enrollmentSteps: EnrollmentStepDTO[]) {
  for (const step of enrollmentSteps) {
    const stepId: string = step.stepId;
    const stepName: EnrollmentStep = step.stepName;
    if (stepName === EnrollmentStep.BULK) {
      const entityNames = (step.stepData as unknown as any[])
        .map((item: any) => item.EntityInformation?.EntityName)
        .filter(Boolean);
      yield put(setEnrollmentType(EnrollmentType.MULTIPLE_ENTITY));
      yield put(uploadTemplateFileSuccess(entityNames));
      break;
    } else {
      const stepData: any = JSON.parse(step.stepData);
      if (stepName === EnrollmentStep.PATH_SELECTION) {
        yield put(setIsDFINAdminSuccess(stepData.isDFINAdmin, stepId));
        continue;
      }
      if (stepName === EnrollmentStep.ENTITY_INFORMATION) {
        yield put(setEntityInfoSuccess(stepData.entityInfo, stepId));
        continue;
      }
      if (stepName === EnrollmentStep.ACCOUNT_ADMINISTRATOR_1) {
        yield put(setAccountAdminOneSuccess(stepData.accountAdminOne, stepId));
        continue;
      }
      if (stepName === EnrollmentStep.ACCOUNT_ADMINISTRATOR_2) {
        yield put(setAccountAdminTwoSuccess(stepData.accountAdminTwo, stepId));
        continue;
      }
    }
  }
}

export function* continueEnrollmentSaga(
  action: ReturnType<typeof continueEnrollment>
) {
  const { enrollmentId } = action.payload;
  try {
    const { data: response } = yield call(
      enrollmentApi.getEnrollmentSteps,
      enrollmentId
    );
    yield call(loadPreviousStepData, response);
    const enrollmentStepIds: EnrollmentStepIds = yield call(
      mapEnrollmentStepIds,
      response
    );
    const lastStep: EnrollmentStep = yield call(getLastStep, enrollmentStepIds);
    yield put(continueEnrollmentSuccess(enrollmentId));
    yield put(setActiveStep(lastStep));
  } catch (error) {
    console.error(error);
    yield put(continueEnrollmentFail());
  }
}

export function* enrollmentSaga() {
  yield takeLatest(CREATE_ENROLLMENT, createEnrollmentSaga);
  yield takeLatest(SET_IS_DFIN_ADMIN, setIsDFINAdminSaga);
  yield takeLatest(SET_ENTITY_INFO, setEntityInfoSaga);
  yield takeLatest(UPLOAD_TEMPLATE_FILE, uploadTemplateFileSaga);
  yield takeLatest(SET_ACCOUNT_ADMIN_ONE, setAccountAdminOneSaga);
  yield takeLatest(SET_ACCOUNT_ADMIN_TWO, setAccountAdminTwoSaga);
  yield takeLatest(ENROLL, enrollSaga);
  yield takeLatest(GET_EDGAR_STATUS, getEdgarStatusSaga);
  yield takeLatest(GET_LATEST_ENROLLMENT, getLatestEnrollmentSaga);
  yield takeLatest(CONTINUE_ENROLLMENT, continueEnrollmentSaga);
}
